import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Link } from 'react-router-dom';
import '../styles/segmentedControl.css';

interface SegmentOption {
  id: string;
  label: string;
  path: string;
}

interface HeaderWithSegmentedControlProps {
  title?: string;
  options: SegmentOption[];
  activeOptionId: string;
  onOptionChange: (optionId: string) => void;
}

const HeaderWithSegmentedControl: React.FC<HeaderWithSegmentedControlProps> = ({
  options,
  activeOptionId,
  onOptionChange
}) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const logoSrc = isDark 
    ? `${process.env['PUBLIC_URL']}/images/flarion-white.svg` 
    : `${process.env['PUBLIC_URL']}/images/flarion-black-white.svg`;

  return (
    <header className={`fixed top-6 left-0 right-0 z-10 px-4 py-3 flex items-center justify-between gap-4 ${
      isDark ? 'text-white' : 'text-gray-800'
    }`}>
      {/* Logo */}
      <div className="flex items-center justify-center h-14 flex-shrink-0">
        <img 
          src={logoSrc}
          alt="Spark Dashboard" 
          className="h-10 w-auto object-contain"
          onError={(e) => {
            // If image fails to load, replace with text logo
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              const textLogo = document.createElement('h1');
              textLogo.className = `text-xl font-semibold ${isDark ? 'text-white' : 'text-black'}`;
              textLogo.textContent = '';
              parent.appendChild(textLogo);
            }
          }}
        />
      </div>

      {/* Segmented Control */}
      <div className={`segmented-control h-14 flex items-center ${isDark ? 'segmented-control-dark' : 'segmented-control-light'}`}>
        {options.map((option) => (
          <Link
            key={option.id}
            to={option.path}
            className={`segment h-12 flex items-center justify-center ${activeOptionId === option.id ? 'active' : ''}`}
            onClick={() => onOptionChange(option.id)}
          >
            {option.label}
          </Link>
        ))}
      </div>

      {/* Theme Toggle and User Menu */}
      <div className="flex items-center h-14 flex-shrink-0">
        {/* Theme Toggle Button */}
        <button
          onClick={toggleTheme}
          className={`p-2 rounded-full h-12 w-12 flex items-center justify-center ${
            isDark ? 'bg-gray-800 text-yellow-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          aria-label="Toggle theme"
        >
          {isDark ? (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
            </svg>
          )}
        </button>

        {/* User Avatar */}
        <div className="ml-4">
          <button className="flex items-center justify-center focus:outline-none">
            <div className={`w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center ${
              isDark ? 'text-gray-900' : 'text-gray-800'
            }`}>
              <span className="font-medium">U</span>
            </div>
          </button>
        </div>
      </div>
    </header>
  );
};

export default HeaderWithSegmentedControl;